import { Action, ActionType } from 'typesafe-actions';
import { ofType } from 'redux-observable';
import { Observable, OperatorFunction, from } from 'rxjs';
import { switchMap, withLatestFrom, catchError, map } from 'rxjs/operators';
import { toReduxError } from '@universal/errors';
import { apiClient, ApiType, ApiPaths } from '@shared/apiClient';
import { fromApiRequest } from '@shared/apiClient/fromApiRequest';
import { ArticleResponse } from '@appTypes/articleType';
import { Actions, ARTICLE_PAGE_SIZE, ArticleListRequest, getArticleListKey, actions } from '@redux/modules/articles';
import { TypedEpic } from './types';

const fetchArticleListEpic: TypedEpic = (action$: Observable<Action<any>>, state$) =>
  action$.pipe(
    ofType(Actions.GET_ARTICLE_LIST),
    withLatestFrom(state$) as unknown as OperatorFunction<
      Action<any>,
      ActionType<typeof actions.getArticleList.request>[]
    >,
    switchMap(([action]: { payload: ArticleListRequest }[]) => {
      const params: Record<string, any> = {
        orderBy: 'published',
        take: ARTICLE_PAGE_SIZE,
      };

      if (action.payload?.category) {
        params.categories = action.payload.category.slug;
      } else {
        params.includeCategories = false;
      }

      if (action.payload?.authorSlug) {
        params.editorSlugs = action.payload.authorSlug;
      }

      return fromApiRequest((signal) =>
        apiClient.get<ArticleResponse>(ApiType.apimNafNoApi, {
          signal,
          url: ApiPaths[ApiType.apimNafNoApi].articles.list,
          params,
        }),
      ).pipe(
        map((response) =>
          actions.getArticleList.success(response.data, {
            category: action.payload?.category,
            authorSlug: action.payload?.authorSlug,
          }),
        ),
        catchError((err) =>
          from([
            actions.getArticleList.failure(toReduxError(err, 'Could not get articles'), {
              category: action.payload?.category,
              authorSlug: action.payload?.authorSlug,
            }),
          ]),
        ),
      );
    }),
  );

const fetchMoreArticlesEpic: TypedEpic = (action$: Observable<Action<any>>, state$) =>
  action$.pipe(
    ofType(Actions.GET_MORE_ARTICLES),
    withLatestFrom(state$) as unknown as OperatorFunction<
      Action<any>,
      ActionType<typeof actions.getMoreArticles.request>[]
    >,
    switchMap(([action]: { payload: ArticleListRequest }[]) => {
      const currentState = state$.value.articles;
      const key = getArticleListKey(action.payload);
      const currentData = currentState.data[key];
      const nextPage = currentData.numberOfLoadedPages + 1;

      const params: Record<string, any> = {
        orderBy: 'published',
        take: ARTICLE_PAGE_SIZE,
        skip: (nextPage - 1) * ARTICLE_PAGE_SIZE,
      };

      if (action.payload?.category) {
        params.categories = action.payload.category.slug;
      } else {
        params.includeCategories = false;
      }

      if (action.payload?.authorSlug) {
        params.editorSlugs = action.payload.authorSlug;
      }

      return fromApiRequest((signal) =>
        apiClient.get<ArticleResponse>(ApiType.apimNafNoApi, {
          signal,
          url: ApiPaths[ApiType.apimNafNoApi].articles.list,
          params,
        }),
      ).pipe(
        map((response) =>
          actions.getMoreArticles.success(response.data, {
            category: action.payload?.category,
            authorSlug: action.payload?.authorSlug,
          }),
        ),
        catchError((err) =>
          from([
            actions.getMoreArticles.failure(toReduxError(err, 'Could not get more articles'), {
              category: action.payload?.category,
              authorSlug: action.payload?.authorSlug,
            }),
          ]),
        ),
      );
    }),
  );

export const epics: TypedEpic[] = [fetchMoreArticlesEpic, fetchArticleListEpic];
